import type { routes } from '@semios/app-platform-banyan-route-definitions'
import {
  getValueTypeFromEmitterType,
  IRRIGATION_APPLIED_VOLUME_VALUE_TYPE_PREFIX,
  type TEmitterType,
} from '@semios/app-platform-values-helpers-and-types'
import type { TGroupedTableSection } from 'App/dashboards/_components/DashboardGroupedTable'
import { getApplicationRateFromFlowRate, type TApplicationRate } from 'App/dashboards/_utils/applicationRate'
import { isNil } from 'lodash'
import type moment from 'moment-timezone'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { apiFetch } from 'utils/apiFetch'
import { getIsoWithoutTheZ } from 'utils/getIsoWithoutTheZ'
import { lngLatStringToLngLatObject } from 'utils/lngLatStringToLngLatObject'

// One emitter within a zone = one row. Volume (mm) + duration (ms) are per emitter; rain + ETc are
// per zone (repeated across its emitter rows), all summed over the window.
// Zone name comes from the zone record (`irrigationZones[zoneId].name`); the emitter is its
// emitterType. A zone's emitters share its name, so grouping/collapsing by zone is contiguous.
export type TIrrigationZoneRow = {
  zoneId: number
  zoneName: string
  emitterType: TEmitterType
  applicationRate: TApplicationRate | null
  volumeMm: number | null
  durationMs: number | null
  rainSum: number | null
  etcSum: number | null
}

type TZone = {
  zoneId: number
  zoneName: string
  emitters: { emitterType: TEmitterType; applicationRate: TApplicationRate | null }[]
  // Blocks representing this zone's ETc (block-scoped for now; a points-scoped ETc value type will
  // replace this once the dashboard skeleton lands).
  blockIds: number[]
  // Nearest precipitation station to the zone, for Rain.
  stationLngLat: string | null
}

type TPropertyZones = {
  propertyId: number
  propertyName: string
  zones: TZone[]
  emitterTypes: Set<TEmitterType>
  stationLngLats: string[]
  blockIds: number[]
}

const squaredDistance = (a: { lng: number; lat: number }, b: { lng: number; lat: number }): number =>
  (a.lng - b.lng) ** 2 + (a.lat - b.lat) ** 2

// Build each selected property's zones with their emitters, ETc blocks, and nearest rain station.
// A zone's blocks/station come from its irrigation device points (points tagged with the zone via
// `irrigationEmitterZoneIds`): their `blockIds` give the ETc blocks, their centroid picks the nearest
// precipitation station (mirrors the legacy nearest-weather-station-by-distance logic client-side).
const getPropertyZones = (
  properties: NonNullable<ReturnType<typeof fieldAssetStore.getState>['properties']>,
  propertyIds: number[],
): TPropertyZones[] =>
  propertyIds
    .map((propertyId) => {
      const property = properties[propertyId]

      if (!property) return null

      const points = Object.values(property.points ?? {})

      // Precipitation stations on the property (points advertising the precipitation value type).
      const precipStations = points
        .filter((p) => (p.valuesTimeseries ?? []).some((v) => String(v) === 'precipitation'))
        .map((p) => ({ lngLat: p.lngLat, coord: lngLatStringToLngLatObject(p.lngLat) }))

      // Group emitters by zone.
      const byZone = new Map<
        number,
        { zoneName: string; emitters: Map<TEmitterType, TApplicationRate | null> }
      >()

      Object.values(property.irrigationZoneEmitters ?? {}).forEach((emitter) => {
        const zoneId = Number(emitter.irrigationZoneId)
        const entry = byZone.get(zoneId)
        const emitterType = emitter.emitterType as TEmitterType
        // Zone name from the zone record; fall back to the emitter's name (same zone label in
        // practice) when irrigationZones is sparse, rather than showing a bare id.
        const zoneName = property.irrigationZones?.[emitter.irrigationZoneId]?.name ?? emitter.name
        const applicationRate = getApplicationRateFromFlowRate(emitter.flowRate)

        if (entry) {
          entry.emitters.set(emitterType, applicationRate)
        } else {
          byZone.set(zoneId, { zoneName, emitters: new Map([[emitterType, applicationRate]]) })
        }
      })

      // Note: no early-return when there are no zones — every selected property still gets a section
      // (with empty rows), so the widget shows a per-property "no data" message rather than dropping it.
      const zones: TZone[] = [...byZone.entries()].map(([zoneId, { zoneName, emitters }]) => {
        // The zone's irrigation device points: tagged with this zone via irrigationEmitterZoneIds.
        const zonePoints = points.filter((p) =>
          (p.irrigationEmitterZoneIds ?? []).some((id) => String(id).startsWith(`${zoneId}|||`)),
        )

        const blockIds = [...new Set(zonePoints.flatMap((p) => p.blockIds ?? []))]
        // Zone location = centroid of its device points, used to pick the nearest rain station.
        const coords = zonePoints.map((p) => lngLatStringToLngLatObject(p.lngLat))

        const centroid =
          coords.length > 0
            ? {
                lng: coords.reduce((s, c) => s + c.lng, 0) / coords.length,
                lat: coords.reduce((s, c) => s + c.lat, 0) / coords.length,
              }
            : null

        let stationLngLat: string | null = null

        if (centroid && precipStations.length > 0) {
          stationLngLat = precipStations
            .slice(1)
            .reduce(
              (nearest, s) =>
                squaredDistance(centroid, s.coord) < squaredDistance(centroid, nearest.coord) ? s : nearest,
              precipStations[0],
            ).lngLat
        }

        return {
          zoneId,
          zoneName,
          emitters: [...emitters.entries()].map(([emitterType, applicationRate]) => ({
            emitterType,
            applicationRate,
          })),
          blockIds,
          stationLngLat,
        }
      })

      // Sorted by zone name so the grouped table's zone-column collapse-repeats stays contiguous.
      zones.sort((a, b) => a.zoneName.localeCompare(b.zoneName))

      return {
        propertyId,
        propertyName: property.propertyName,
        zones,
        emitterTypes: new Set(zones.flatMap((z) => z.emitters.map((e) => e.emitterType))),
        stationLngLats: [...new Set(zones.map((z) => z.stationLngLat).filter((s) => !!s))],
        blockIds: [...new Set(zones.flatMap((z) => z.blockIds))],
      }
    })
    .filter((p): p is TPropertyZones => p !== null)

// Sum a numeric timeseries (entry.value is a plain number, e.g. ETc) over the window.
const sumNumberSeries = (timeseries: { value: number | null }[]): number | null =>
  timeseries.reduce<number | null>((acc, e) => (isNil(e?.value) ? acc : (acc ?? 0) + e.value), null)

export type TIrrigationZoneActivityResult = {
  sections: TGroupedTableSection<TIrrigationZoneRow>[]
  loading: boolean
  error: Error | null
  lastFetchedAt: Date | null
  refresh: () => void
}

// Fetches, for every selected property's zones in one /values call: applied irrigation volume +
// on-duration (irrigationZones scope), precipitation at each zone's nearest station (points scope),
// and ETc for each zone's blocks (blocks scope). Each emitter becomes a row; rain + ETc are
// zone-level (repeated across the zone's emitter rows). All totals are summed over the window.
export const useIrrigationZoneActivity = ({
  propertyIds,
  dateFrom,
  dateTo,
  includeRain = false,
  includeEtc = false,
  refreshToken = 0,
}: {
  propertyIds: number[]
  dateFrom: moment.Moment
  dateTo: moment.Moment
  includeRain?: boolean
  includeEtc?: boolean
  refreshToken?: number
}): TIrrigationZoneActivityResult => {
  const properties = fieldAssetStore.useSelector((s) => s.properties)
  const [data, setData] = useState<routes.Values.Response | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null)
  const [refreshNonce, setRefreshNonce] = useState(0)
  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), [])
  const dateFromMs = +dateFrom
  const dateToMs = +dateTo
  // Content key so a caller passing a fresh propertyIds array each render doesn't churn this memo
  // (and therefore the fetch effect, which keys on propertyZones). propertyZones is the single
  // reference-stable input the effect depends on.
  const propertyIdsKey = propertyIds.join('|')

  // Keyed by propertyIdsKey (content) rather than the propertyIds array reference, so it stays stable
  // across renders when the selection is unchanged.
  const propertyZones = useMemo(
    () => (properties ? getPropertyZones(properties, propertyIds) : []),
    [properties, propertyIdsKey],
  )

  useEffect(() => {
    const zoneIds = new Set<number>()
    const stationLngLats = new Set<string>()
    const blockIds = new Set<number>()
    const irrigationValuesRequested: Record<string, { preferredAggregationInterval: 'DAILY' }> = {}

    propertyZones.forEach((p) => {
      p.zones.forEach((z) => zoneIds.add(z.zoneId))

      p.emitterTypes.forEach((emitterType) => {
        const key = getValueTypeFromEmitterType(IRRIGATION_APPLIED_VOLUME_VALUE_TYPE_PREFIX, emitterType)

        irrigationValuesRequested[key] = { preferredAggregationInterval: 'DAILY' }
      })

      if (includeRain) p.stationLngLats.forEach((s) => stationLngLats.add(s))

      if (includeEtc) p.blockIds.forEach((b) => blockIds.add(b))
    })

    if (zoneIds.size === 0) {
      setData(null)

      setLoading(false)

      setError(null)

      return
    }

    let cancelled = false

    setLoading(true)

    setError(null)

    const body = {
      dateFrom: getIsoWithoutTheZ(dateFrom),
      dateTo: getIsoWithoutTheZ(dateTo),
      // Tag the call's source (as pest, Data Export and Property Playback do) so dashboard-driven
      // /values load is attributable in request logging. A declared optional enum on the Values
      // request schema (value-type-definitions/valuesTimeseriesTypes) that the API validates but
      // never consumes — so an invalid value 422s rather than being silently ignored.
      featureReferer: 'DASHBOARD',
      irrigationZones: { irrigationZoneIds: [...zoneIds], valuesRequested: irrigationValuesRequested },
      ...(includeRain && stationLngLats.size > 0
        ? {
            points: {
              lngLats: [...stationLngLats],
              valuesRequested: { precipitation: { preferredAggregationInterval: 'DAILY' } },
            },
          }
        : {}),
      ...(includeEtc && blockIds.size > 0
        ? {
            blocks: {
              blockIds: [...blockIds],
              valuesRequested: { evapotranspirationETc: { preferredAggregationInterval: 'DAILY' } },
            },
          }
        : {}),
    } as routes.Values.Request

    apiFetch({ url: '/values', body })
      .then((res) => {
        if (cancelled) return

        setData(res)

        setLastFetchedAt(new Date())
      })
      .catch((err) => {
        if (cancelled) return

        setError(err instanceof Error ? err : new Error(String(err)))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
    // propertyZones already reflects propertyIds content (it's keyed by propertyIdsKey), so it's the
    // single reference-stable input here — no separate propertyIdsKey dep needed.
  }, [propertyZones, dateFromMs, dateToMs, includeRain, includeEtc, refreshNonce, refreshToken])

  const sections = useMemo<TGroupedTableSection<TIrrigationZoneRow>[]>(() => {
    return propertyZones.map((p) => ({
      propertyId: p.propertyId,
      propertyName: p.propertyName,
      rows: p.zones.flatMap((zone) => {
        // Rain: the zone's nearest station's precipitation summed over the window.
        const rainTimeseries = (
          zone.stationLngLat
            ? (data?.points?.[zone.stationLngLat]?.values?.precipitation?.[0]?.timeseries ?? [])
            : []
        ) as { value: { sum: number | null } | null }[]

        const rainSum = rainTimeseries.reduce<number | null>(
          (acc, e) => (isNil(e?.value?.sum) ? acc : (acc ?? 0) + (e.value?.sum ?? 0)),
          null,
        )

        // ETc: average the zone's blocks' windowed totals (blocks on a shared station carry the same
        // value, so averaging a multi-block zone gives one representative depth, not an inflated sum).
        const blockTotals = zone.blockIds
          .map((blockId) => {
            const ts = (data?.blocks?.[blockId]?.values?.evapotranspirationETc?.[0]?.timeseries ?? []) as {
              value: number | null
            }[]

            return sumNumberSeries(ts)
          })
          .filter((t) => t !== null)

        const etcSum =
          blockTotals.length > 0 ? blockTotals.reduce((s, t) => s + t, 0) / blockTotals.length : null

        return zone.emitters
          .slice()
          .sort((a, b) => a.emitterType.localeCompare(b.emitterType))
          .map(({ emitterType, applicationRate }) => {
            const valueKey = getValueTypeFromEmitterType(
              IRRIGATION_APPLIED_VOLUME_VALUE_TYPE_PREFIX,
              emitterType,
            ) as keyof NonNullable<NonNullable<routes.Values.Response['irrigationZones']>[string]>['values']

            // The irrigationZones timeseries element is a union (on/off intervals vs volume/duration);
            // for the applied-volume value type it carries { duration, volume_mm }. Cast to that arm.
            const timeseries = (data?.irrigationZones?.[zone.zoneId]?.values?.[valueKey]?.[0]?.timeseries ??
              []) as { value: { duration: number | null; volume_mm: number | null } | null }[]

            let volumeMm: number | null = null
            let durationMs: number | null = null

            timeseries.forEach((entry) => {
              const value = entry?.value

              if (!isNil(value?.volume_mm)) volumeMm = (volumeMm ?? 0) + (value?.volume_mm ?? 0)

              if (!isNil(value?.duration)) durationMs = (durationMs ?? 0) + (value?.duration ?? 0)
            })

            return {
              zoneId: zone.zoneId,
              zoneName: zone.zoneName,
              emitterType,
              applicationRate,
              volumeMm,
              durationMs,
              rainSum,
              etcSum,
            }
          })
      }),
    }))
  }, [propertyZones, data])

  return useMemo(
    () => ({ sections, loading, error, lastFetchedAt, refresh }),
    [sections, loading, error, lastFetchedAt, refresh],
  )
}
